import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlertBox,
  AlertBoxDismissButton,
  Button,
  FlexBox,
  Link,
  Typography,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { pricingOperations, pricingSelectors } from '../../state/pricing';
import { RootState } from '../../types';
import useIdentityContext from '../../hooks/useIdentityContext';

export const NegativeCouponWarning = () => {
  const [couponMessages, sharedActionMessages] = useTranslations([
    'couponMessages',
    'sharedActionMessages',
  ]);

  const dispatch = useDispatch();
  const {
    identity: { isAnonymousUser },
  } = useIdentityContext();

  const isNegativeDiscountApplied = useSelector((state: RootState) =>
    pricingSelectors.selectIsNegativeDiscountApplied(state.pricing)
  );

  const previouslyAppliedDiscountCodeName: string | undefined = useSelector(
    (state: RootState) =>
      pricingSelectors.selectPreviouslyAppliedDiscountCodeName(state.pricing)
  );

  const onRemoveCodeClick = useCallback(
    (previousCouponCode: string | undefined) => {
      if (previousCouponCode) {
        dispatch(
          pricingOperations.setCoupon(previousCouponCode, isAnonymousUser)
        );
      } else {
        dispatch(pricingOperations.clearCoupon());
      }
    },
    [isAnonymousUser, dispatch]
  );

  if (!isNegativeDiscountApplied) return null;

  return (
    <AlertBox skin="warning" mt="3" data-testid="negative-coupon-warning">
      <FlexBox justifyContent="space-between">
        <Typography>{couponMessages.decreasedSavingsWarning}</Typography>
        <Link
          render={p => (
            <Button
              skin="link"
              className={p.className}
              onClick={() =>
                onRemoveCodeClick(previouslyAppliedDiscountCodeName)
              }
            >
              {p.children}
            </Button>
          )}
        >
          {couponMessages.removeCode}
        </Link>
      </FlexBox>
      <AlertBoxDismissButton accessibleText={sharedActionMessages.close} />
    </AlertBox>
  );
};
